import { StarIcon } from "@heroicons/react/20/solid";
import { memo } from "react";
import { ExpandableText } from "@/core/components/expandable-text";
import { Button } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { REVIEW_PLATFORM_TO_DISPLAY } from "@/activity-feed/constants";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import { useGetReview } from "@/reviews/query";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { DetailCard } from "./common";
import FacebookLogoSrc from "@/assets/images/facebook-logo.png";
import GoogleLogoSrc from "@/assets/images/google-logo.png";
import TripAdvisorLogoSrc from "@/assets/images/trip-advisor-logo.jpeg";
import YelpLogoSrc from "@/assets/images/yelp-icon.png";

const PLATFORM_TO_ICON: Record<string, string> = {
  google: GoogleLogoSrc,
  facebook: FacebookLogoSrc,
  yelp: YelpLogoSrc,
  tripadvisor: TripAdvisorLogoSrc,
};

interface ReviewDetailCardProps {
  reviewId: string;
  inView: boolean;
  onOpenEventModal: () => void;
}

export const ReviewDetailCard = memo(
  ({ reviewId, inView, onOpenEventModal }: ReviewDetailCardProps) => {
    const { businessId } = useBusiness();
    const { data: review, isLoading } = useGetReview(
      businessId,
      reviewId,
      inView,
    );

    // Show loading only if we don't have data yet
    if (!review && (!inView || isLoading)) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.five_star_review}>
          <Stack gap="sm" fullWidth>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-64" />
          </Stack>
        </DetailCard>
      );
    }

    if (!review) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.five_star_review}>
          <Stack gap="sm" fullWidth>
            <Typography variant="subtitleW2">Review Details</Typography>
            <Typography variant="bodyW3" textColor="secondary">
              Review information unavailable
            </Typography>
          </Stack>
        </DetailCard>
      );
    }

    const platformIconSrc = review.platform
      ? PLATFORM_TO_ICON[review.platform]
      : undefined;
    const rating = Math.max(0, Math.min(5, Math.round(review.rating ?? 0)));

    return (
      <DetailCard eventType={SPIFF_RULE_EVENTS.five_star_review}>
        <Stack direction="row" align="center" gap="sm">
          <Stack gap="sm" fullWidth>
            <Stack direction="row" align="center" gap="sm">
              {platformIconSrc && (
                <img
                  src={platformIconSrc}
                  alt={`${review.platform || "Google"} logo`}
                  className="h-4 w-4 object-contain"
                />
              )}
              <Typography variant="captionW1" textColor="secondary">
                {REVIEW_PLATFORM_TO_DISPLAY[
                  review.platform as keyof typeof REVIEW_PLATFORM_TO_DISPLAY
                ] || "Google"}{" "}
                review
              </Typography>
            </Stack>
            <Stack direction="row" align="center" gap="sm" fullWidth>
              <Typography variant="subtitleW2">
                {review.reviewer_name}
              </Typography>
              <Stack direction="row" gap="xs" align="center" className="pb-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <StarIcon
                    key={i}
                    className={cn("h-4 w-4 text-gold-30", {
                      "opacity-30": i >= rating,
                    })}
                  />
                ))}
              </Stack>
            </Stack>
            {review.body && (
              <ExpandableText
                text={review.body}
                maxLines={3}
                variant="bodyW2"
                textColor="secondary"
                gradientColor="#f9f9f9"
              />
            )}
          </Stack>
          <Button variant="secondary" size="sm" onClick={onOpenEventModal}>
            Add users
          </Button>
        </Stack>
      </DetailCard>
    );
  },
);

ReviewDetailCard.displayName = "ReviewDetailCard";
